import React, { useState } from "react";
import { format, isToday, isPast } from "date-fns";
import { nl } from "date-fns/locale";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Plus, Edit, Package, CheckCircle2, Calendar } from "lucide-react";
import { PackagePickupLog } from "@/types/packagePickup";
import PackagePickupForm from "./PackagePickupForm";
import { usePermissions } from "@/context/PermissionsContext";
import { cn } from "@/lib/utils";

interface PackagePickupCalendarProps {
  weekDays: Date[];
  logs: PackagePickupLog[];
  isLoading: boolean;
}

const PackagePickupCalendar = ({ weekDays, logs, isLoading }: PackagePickupCalendarProps) => {
  const [editingLog, setEditingLog] = useState<PackagePickupLog | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const { hasPermission } = usePermissions();
  
  const canWrite = hasPermission("packagePickup", "write");

  // Create a map of logs by date for quick lookup
  const logsByDate = logs.reduce((acc, log) => {
    acc[log.pickup_date] = log;
    return acc;
  }, {} as Record<string, PackagePickupLog>);

  const handleAddTime = (date: Date) => {
    if (!canWrite) return;
    
    setSelectedDate(format(date, "yyyy-MM-dd"));
    setEditingLog(null);
    setShowForm(true);
  };

  const handleEditTime = (log: PackagePickupLog) => {
    if (!canWrite) return;
    
    setEditingLog(log);
    setSelectedDate("");
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingLog(null);
    setSelectedDate("");
  };

  const formatTimeDisplay = (time: string) => {
    try {
      const [hours, minutes] = time.split(":");
      const hour24 = parseInt(hours);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${period}`;
    } catch {
      return time;
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-5 gap-4">
        {weekDays.map((day, index) => (
          <Card key={index} className="p-4 h-40 animate-pulse bg-muted">
            <div className="h-4 bg-muted-foreground/20 rounded mb-2"></div>
            <div className="h-3 bg-muted-foreground/20 rounded mb-1"></div>
            <div className="h-3 bg-muted-foreground/20 rounded w-2/3"></div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-5 gap-4">
        {weekDays.map((day) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const log = logsByDate[dateStr];
          const dayName = format(day, "EEEE", { locale: nl });
          const dayNumber = format(day, "d");
          const isCurrentDay = isToday(day);
          const isDayPast = isPast(day) && !isCurrentDay;
          
          return (
            <Card 
              key={dateStr} 
              className={cn(
                "relative p-4 h-40 transition-all duration-500 transform-gpu perspective-1000",
                "shadow-[0_4px_20px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]",
                "hover:shadow-[0_20px_40px_rgba(0,0,0,0.15),0_8px_16px_rgba(0,0,0,0.08)]",
                "hover:-translate-y-3 hover:scale-[1.02] hover:rotate-x-2",
                "bg-gradient-to-br from-card via-card to-card/95",
                "border-2 border-border/50 hover:border-border",
                {
                  // Today's card - extra prominent 3D effect with glow
                  "ring-4 ring-primary/30 shadow-[0_8px_32px_rgba(59,130,246,0.25),0_4px_8px_rgba(59,130,246,0.1)] bg-gradient-to-br from-primary/5 via-card to-primary/10": isCurrentDay,
                  "hover:ring-primary/50 hover:shadow-[0_24px_48px_rgba(59,130,246,0.35),0_12px_24px_rgba(59,130,246,0.2)]": isCurrentDay,
                  
                  // Past days with pickup - completed green 3D effect
                  "shadow-[0_6px_24px_rgba(34,197,94,0.12),0_2px_6px_rgba(34,197,94,0.06)] bg-gradient-to-br from-green-50/50 via-card to-green-50/30": isDayPast && log,
                  "hover:shadow-[0_16px_32px_rgba(34,197,94,0.2),0_8px_16px_rgba(34,197,94,0.1)]": isDayPast && log,
                  
                  // Past days without pickup - sunken/inset 3D effect
                  "shadow-[inset_0_2px_8px_rgba(0,0,0,0.1)] bg-gradient-to-br from-muted/30 via-muted/20 to-muted/10 opacity-70": isDayPast && !log,
                  "hover:shadow-[inset_0_4px_12px_rgba(0,0,0,0.15)] hover:translate-y-0 hover:scale-100": isDayPast && !log,
                  
                  // Future days without pickup - interactive hover state
                  "hover:bg-gradient-to-br hover:from-accent/20 hover:via-accent/10 hover:to-accent/5": !log && !isDayPast,
                  
                  // Future days with pickup - scheduled blue 3D effect
                  "shadow-[0_6px_24px_rgba(59,130,246,0.12),0_2px_6px_rgba(59,130,246,0.06)] bg-gradient-to-br from-blue-50/50 via-card to-blue-50/30": !isDayPast && log && !isCurrentDay,
                  "hover:shadow-[0_16px_32px_rgba(59,130,246,0.2),0_8px_16px_rgba(59,130,246,0.1)]": !isDayPast && log && !isCurrentDay,
                }
              )}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-center">
                    <div className={cn(
                      "text-sm font-medium uppercase tracking-wide capitalize",
                      isCurrentDay ? "text-primary" : "text-muted-foreground"
                    )}>
                      {dayName}
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={cn(
                        "text-lg font-semibold",
                        isCurrentDay && "text-primary"
                      )}>
                        {dayNumber}
                      </div>
                      {isCurrentDay && (
                        <Badge variant="secondary" className="text-xs py-0 px-1 shadow-lg bg-gradient-to-r from-primary/20 to-primary/30 animate-pulse border border-primary/30">
                          Vandaag
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {log && canWrite && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditTime(log)}
                      className="p-1 h-auto hover:bg-accent/80 hover:shadow-lg hover:scale-110 transition-all duration-300 rounded-lg"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                {log ? (
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "h-3 w-3 rounded-full shadow-lg border-2 border-white transition-all duration-300 hover:scale-125",
                          isDayPast ? "bg-gradient-to-br from-green-400 to-green-600 shadow-green-500/50" : "bg-gradient-to-br from-blue-400 to-blue-600 shadow-blue-500/50"
                        )} />
                        <CheckCircle2 className={cn(
                          "h-5 w-5 drop-shadow-lg transition-all duration-300 hover:scale-110",
                          isDayPast ? "text-green-500" : "text-blue-500"
                        )} />
                      </div>
                      <Badge 
                        variant={isDayPast ? "secondary" : "default"} 
                        className={cn(
                          "text-xs shadow-md transition-all duration-300 hover:shadow-lg hover:scale-105",
                          isDayPast 
                            ? "bg-gradient-to-r from-green-500/20 to-green-600/30 text-green-700 border-green-300" 
                            : "bg-gradient-to-r from-blue-500/20 to-blue-600/30 text-blue-700 border-blue-300"
                        )}
                      >
                        {isDayPast ? "Voltooid" : "Gepland"}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-primary" />
                        <span className="font-medium">{log.pickup_time}</span>
                        <span className="text-muted-foreground text-xs">
                          ({formatTimeDisplay(log.pickup_time)})
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{log.aantal_rolcontainers}</span>
                        <span className="text-muted-foreground">
                          container{log.aantal_rolcontainers !== 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      {log.notes && (
                        <div className="bg-muted/50 rounded-md p-2 mt-2">
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {log.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                    <div className="flex flex-col items-center gap-2">
                      <Calendar className="h-8 w-8 opacity-50" />
                      <p className="text-xs text-center">Geen ophaling gepland</p>
                    </div>
                    {canWrite && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddTime(day)}
                        className="mt-3 w-full text-xs hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:shadow-lg hover:scale-105 hover:-translate-y-1 border-2 hover:border-primary"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Tijd Toevoegen
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <PackagePickupForm 
        open={showForm}
        onOpenChange={handleCloseForm}
        editingLog={editingLog}
        initialDate={selectedDate}
      />
    </>
  );
};

export default PackagePickupCalendar;